import { createApiClient, downloadCsv, type Distribution, type Invitation, type StayStatus } from '@rooming/web-core';

// Typed calls of the backoffice (API_SPECIFICATION.md). One client instance for the app; every screen goes through it,
// so CSRF, Idempotency-Key and RFC 9457 handling are never re-implemented in a page.

export const api = createApiClient();

/** `TENANT_ADMIN` is the one internal role. The others are legacy: no permission, never granted, only revocable. */
export type StaffRole = 'TENANT_ADMIN' | 'HOTEL_ADMIN' | 'OPERATOR' | 'SUPPORT' | 'AUDITOR';
export type SessionStage = 'MFA_PENDING' | 'CREDENTIAL_CHANGE_PENDING' | 'ACTIVE';

export type ScopedGrant =
  | { id: string; role: 'HOTEL_USER'; stayId: string; hotelId: string }
  | { id: string; role: 'COORDINATOR'; groupId: string };

export interface Me {
  id: string;
  kind: 'STAFF' | 'PASSENGER' | 'APODERADO' | 'EXTERNAL';
  email: string | null;
  displayName: string;
  roles: StaffRole[];
  grants: ScopedGrant[];
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface School {
  id: string;
  name: string;
  city: string | null;
  status: 'ACTIVE' | 'ARCHIVED';
}
export interface Contract {
  id: string;
  schoolId: string;
  contractNumber: string;
  seasonLabel: string | null;
  status: 'ACTIVE' | 'CLOSED';
}
export interface Group {
  id: string;
  contractId: string;
  code: string;
  name: string;
  status: string;
  departureDate: string | null;
  returnDate: string | null;
  /** Passenger exchanges switched on for THIS group (an administrator edits it). */
  swapsEnabled: boolean;
  /** False when this deployment has exchanges off (kept for version 2): the card is not shown. */
  swapsAvailable?: boolean;
}
export interface Hotel {
  id: string;
  name: string;
  city: string;
  countryCode: string;
  timezone: string;
  status: 'ACTIVE' | 'ARCHIVED';
  /** Receive the confirmation email of the hotel's stays (prompt18). */
  contactEmails: string[];
}
export interface Stay {
  id: string;
  groupId: string;
  hotelId: string;
  checkInAt: string;
  checkOutAt: string;
  selectionOpensAt: string | null;
  selectionClosesAt: string | null;
  status: StayStatus;
  version: number;
}
/** The administrator's message for the hotel of a stay (owner, 2026-10-07). */
export interface HotelMessage {
  text: string | null;
  updatedAt: string | null;
}
/** The hotel's terms and conditions (owner, 2026-10-20): a link and/or a text; both null = none loaded. */
export interface HotelTerms {
  hotelId: string;
  url: string | null;
  text: string | null;
  /** The terms as a file (PDF, image or Word), or null. */
  file: { name: string; type: string; size: number } | null;
  version: number;
  updatedAt: string | null;
}
/** One request for a bed that needs an administrator's approval (passenger with undefined sex). */
export interface PlacementRequestItem {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED' | 'SUPERSEDED';
  createdAt: string;
  resolvedAt: string | null;
  note: string | null;
  stay: { id: string; groupCode: string; groupName: string; hotelName: string };
  member: { id: string; firstNames: string; lastNames: string };
  room: { label: string; sexRestriction: 'FEMALE' | 'MALE' | 'ANY'; position: number };
  /** The bed is already taken: approving would fail. */
  slotTaken: boolean;
}
export interface ExternalStay extends Stay {
  hotel: { name: string; city: string; timezone: string } | null;
  group: { code: string; name: string } | null;
  access: 'HOTEL_USER' | 'COORDINATOR';
  /** Only the hotel receives it (null for a guide, and when there is none). */
  hotelMessage: HotelMessage | null;
  /** Only the hotel receives it: its terms and conditions, which it loads and changes itself. */
  hotelTerms: HotelTerms | null;
}
export type RoomAudience = 'PASSENGER' | 'COMPANION';
export interface Room {
  id: string;
  stayId: string;
  label: string;
  roomTypeCode: string | null;
  capacity: number;
  sexRestriction: 'FEMALE' | 'MALE' | 'ANY';
  /** Who the room is for: the students or the companions (the parents who travel). */
  audience: RoomAudience;
  physicalLabel: string | null;
  version: number;
}
export interface Member {
  id: string;
  groupId: string;
  role: 'PASSENGER' | 'APODERADO' | 'COMPANION';
  firstNames: string;
  lastNames: string;
  documentType: string;
  documentHint: string;
  sex: 'FEMALE' | 'MALE' | 'UNDEFINED';
  status: 'ACTIVE' | 'CANCELLED';
  userId: string | null;
  /** The group's ONE designated signing guardian (confirms the distribution). */
  isSigner: boolean;
  /** The member's login account (an administrator resets its password), if any. */
  accountId: string | null;
  /** Whether a date of birth is loaded (it is shown only in the rooming list). */
  hasBirthDate: boolean;
  /** PENDING = has not chosen their own password yet (share the group link with them); null = no account or not told. */
  accountState?: 'ACTIVATED' | 'PENDING' | null;
}
export interface ImportPreview {
  id: string;
  groupId: string;
  status: string;
  totals: { total: number; create: number; update: number; unchanged: number; error: number };
  warnings: string[];
  /** Why the whole file is refused beyond row errors (`falta_el_firmante`). */
  blockers: string[];
  rows: {
    rowNumber: number;
    outcome: 'CREATE' | 'UPDATE' | 'UNCHANGED' | 'ERROR';
    role: string | null;
    /** The row names the group's signatory. */
    isSigner: boolean;
    firstNames: string | null;
    lastNames: string | null;
    documentHint: string | null;
    sex: string | null;
    /** Optional `correo` column (prompt18). */
    email: string | null;
    proposedUsername: string | null;
    issues: string[];
  }[];
}
export interface ImportCommit {
  batchId: string;
  created: { memberId: string; role: string; username: string | null }[];
  updated: number;
  unchanged: number;
}
export interface StaffUser {
  id: string;
  email: string;
  displayName: string;
  status: 'ACTIVE' | 'DISABLED';
  locked: boolean;
  mfaEnrolled: boolean;
  roles: StaffRole[];
}
export interface ExternalUser {
  id: string;
  email: string;
  displayName: string;
  status: 'ACTIVE' | 'DISABLED';
  locked: boolean;
  mfaEnrolled: boolean;
  /** HOTEL: stays of one hotel. COORDINATOR = the trip's GUIDE: groups. Neither uses a second factor. Fixed. */
  type: 'HOTEL' | 'COORDINATOR';
  grants: ScopedGrant[];
}
/** One passenger of the group and where they are (prompt10 §10–§12). */
export interface RosterEntry {
  memberId: string;
  firstNames: string;
  lastNames: string;
  sex: string;
  /** A student or a companion (a parent who travels). */
  role: 'PASSENGER' | 'COMPANION';
  room: string | null;
  physicalRoom: string | null;
  /** Only in the explicit, audited read of documents (signatory, guide, hotel). */
  document?: { type: string; number: string };
  /** PENDING = has not chosen their own password yet: share the group link with them (DR-408). Not sent to the hotel. */
  accountState?: 'ACTIVATED' | 'PENDING';
}
interface NoticeBase {
  id: string;
  stayId: string;
  read: boolean;
  createdAt: string;
}
/** An in-app notification (prompt10 §5; the hotel message since 2026-10-07). */
export type Notice = DistributionNotice | HotelMessageNotice | PlacementNotice;
/** An administrator is told that a passenger with undefined sex asked for a bed (owner, 2026-10-20). */
export interface PlacementNotice extends NoticeBase {
  kind: 'PLACEMENT_REQUESTED';
  payload: {
    requestId: string;
    group: { code: string; name: string };
    hotel: { name: string };
    passenger: string;
    room: string;
    requestedAt: string;
  };
}
export interface HotelMessageNotice extends NoticeBase {
  kind: 'HOTEL_MESSAGE';
  payload: {
    group: { code: string; name: string };
    hotel: { name: string; city: string; timezone: string };
    stay: { checkInAt: string; checkOutAt: string };
    message: string;
    savedAt: string;
  };
}
export interface DistributionNotice extends NoticeBase {
  kind: 'DISTRIBUTION_CONFIRMED';
  payload: {
    group: { code: string; name: string };
    school: string;
    contract: string;
    hotel: { name: string; city: string; timezone: string };
    stay: { checkInAt: string; checkOutAt: string };
    confirmedBy: string;
    confirmedAt: string;
    status: { assigned: number; eligible: number; unassigned: number };
    /** Room summary (notices created since 2026-10-05). */
    rooms?: { total: number; inUse: number; places: number; freePlaces: number };
  };
}
export interface MemberChanges {
  firstNames?: string;
  lastNames?: string;
  sex?: Member['sex'];
  document?: { type: string; number: string };
  /** Optional; null (empty) removes it. */
  birthDate?: string | null;
}
export interface AuditEvent {
  id: string;
  occurredAt: string;
  actorUserId: string | null;
  actorName: string | null;
  action: string;
  entityType: string;
  entityId: string;
  reason: string | null;
  details: Record<string, unknown>;
}
export interface HistoryEntry {
  id: string;
  stayId: string;
  occurredAt: string;
  action: string;
  memberName: string;
  fromRoom: string | null;
  toRoom: string | null;
  stayVersion: number;
  actorKind: string;
  actorName: string | null;
  reason: string | null;
}

/** A hotel administrator (ADR-0013): staff limited to the hotels listed here (at least one). */
export interface HotelAdmin {
  id: string;
  email: string;
  displayName: string;
  status: 'ACTIVE' | 'DISABLED';
  locked: boolean;
  mfaEnrolled: boolean;
  hotels: { id: string; name: string; city: string }[];
}

/** One entry of the executive audit: already told in plain Spanish by the server (ADR-0013). */
export interface ExecutiveAuditItem {
  id: string;
  source: 'actions' | 'beds';
  occurredAt: string;
  hotel: { id: string; name: string } | null;
  actor: { name: string; email: string | null; role: string | null } | null;
  category: string;
  summary: string;
  reason: string | null;
  details: { label: string; value: string }[];
}

/** An access event of the audit module: logins, failures, logouts, second factor, resets. */
export interface AccessEvent {
  id: string;
  occurredAt: string;
  userId: string;
  accountName: string;
  accountKind: 'STAFF' | 'PASSENGER' | 'APODERADO' | 'EXTERNAL';
  eventType: string;
}
export interface NewSigner {
  firstNames: string;
  lastNames: string;
  sex: Member['sex'];
  document: { type: string; number: string };
}

const get = async <T>(path: string): Promise<T> => (await api.get<T>(path)).data;
const send = async <T>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, body?: unknown): Promise<T> =>
  (await api.send<T>(method, path, body)).data;
const query = (params: Record<string, string | undefined>): string => {
  const search = new URLSearchParams(
    Object.entries(params).filter((entry): entry is [string, string] => entry[1] !== undefined),
  );
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
};

/** Reads every page of a cursor-paginated list, up to `max` items (lists the backoffice shows whole, e.g. hotels). */
async function all<T>(path: string, params: Record<string, string | undefined> = {}, max = 1000): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | undefined;
  do {
    const page = await get<Page<T>>(`${path}${query({ ...params, limit: '200', cursor })}`);
    items.push(...page.items);
    cursor = page.nextCursor ?? undefined;
  } while (cursor !== undefined && items.length < max);
  return items;
}

export const backoffice = {
  // ── Session ──
  session: () => get<{ stage: SessionStage; csrfToken: string }>('/auth/session'),
  /** `country` only after the API asked for it (`elegir-pais`): the same email has an account in both countries. */
  login: (email: string, password: string, country?: 'cl' | 'py') =>
    send<{ nextStep: 'MFA_ENROLL' | 'MFA_VERIFY' | 'CREDENTIAL_CHANGE' | 'NONE'; csrfToken: string }>(
      'POST',
      '/auth/sessions',
      { email, password, ...(country === undefined ? {} : { country }) },
    ),
  startEnrollment: () => send<{ secret: string; otpauthUri: string }>('POST', '/auth/mfa/enrollment'),
  confirmEnrollment: (code: string) =>
    send<{ csrfToken: string; recoveryCodes: string[] }>('POST', '/auth/mfa/enrollment/confirmation', { code }),
  verify: (proof: { code: string } | { recoveryCode: string }) =>
    send<{ csrfToken: string }>('POST', '/auth/mfa/verification', proof),
  /** Mandatory after an administrator set a provisional password (hotel users and guides). */
  chooseOwnPassword: (newPassword: string) =>
    send<{ csrfToken: string }>('POST', '/auth/credential-change', { newPassword }),
  logout: () => send<undefined>('DELETE', '/auth/sessions/current'),
  me: () => get<Me>('/me'),

  // ── Catalog ──
  schools: () => all<School>('/schools'),
  createSchool: (body: { name: string; city?: string }) => send<School>('POST', '/schools', body),
  contracts: () => all<Contract>('/contracts'),
  groups: () => all<Group>('/groups'),
  group: (id: string) => get<Group>(`/groups/${id}`),
  /** prompt21: school + contract NUMBER; the API finds the contract or creates it for that school. */
  createGroup: (body: { schoolId: string; contractNumber: string; code: string; name: string }) =>
    send<Group>('POST', '/groups', body),
  setGroupExchanges: (id: string, enabled: boolean, reason: string) =>
    send<Group>('PUT', `/groups/${id}/exchanges`, { enabled, reason }),
  hotels: () => all<Hotel>('/hotels'),
  createHotel: (body: { name: string; city: string; countryCode: string; timezone: string; contactEmails: string[] }) =>
    send<Hotel>('POST', '/hotels', body),
  setHotelEmails: (id: string, contactEmails: string[]) => send<Hotel>('PATCH', `/hotels/${id}`, { contactEmails }),

  // ── Stays and rooms ──
  stays: (filter: { groupId?: string } = {}) => all<Stay>('/stays', filter),
  createStay: (body: { groupId: string; hotelId: string; checkInAt: string; checkOutAt: string }) =>
    send<Stay>('POST', '/stays', body),
  openStay: (id: string) => send<Stay>('POST', `/stays/${id}/opening`),
  closeStay: (id: string, reason: string) => send<Stay>('POST', `/stays/${id}/closure`, { reason }),
  rooms: (stayId: string) => all<Room>(`/stays/${stayId}/rooms`),
  replaceRoom: (
    stayId: string,
    body: {
      roomId: string;
      cause: 'AVERIA' | 'BLOQUEO' | 'CAPACIDAD' | 'OTRO';
      reason: string;
      replacement?: { label: string; capacity: number; sexRestriction: Room['sexRestriction'] };
    },
  ) =>
    send<{ retiredRoomId: string; newRoomId: string | null; relocated: number; stayVersion: number }>(
      'POST',
      `/stays/${stayId}/room-replacements`,
      body,
    ),
  createRooms: (
    stayId: string,
    rooms: {
      label: string;
      capacity: number;
      sexRestriction: Room['sexRestriction'];
      roomTypeCode?: string;
      audience?: RoomAudience;
    }[],
  ) => send<unknown>('POST', `/stays/${stayId}/rooms`, { rooms }),
  setPhysicalLabel: (roomId: string, physicalLabel: string | null, reason?: string) =>
    send<Room>('PUT', `/rooms/${roomId}/physical-label`, {
      physicalLabel,
      ...(reason === undefined ? {} : { reason }),
    }),
  externalStays: () => get<{ items: ExternalStay[] }>('/external/stays'),
  hotelMessage: (stayId: string) => get<HotelMessage>(`/stays/${stayId}/hotel-message`),
  hotelTerms: (stayId: string) => get<HotelTerms>(`/stays/${stayId}/hotel-terms`),
  setHotelTerms: (stayId: string, terms: { url: string | null; text: string | null }) =>
    send<HotelTerms>('PUT', `/stays/${stayId}/hotel-terms`, terms),
  setHotelTermsFile: (stayId: string, file: File) =>
    api.sendFile<HotelTerms>(`/stays/${stayId}/hotel-terms/file`, file),
  removeHotelTermsFile: (stayId: string) => send<HotelTerms>('DELETE', `/stays/${stayId}/hotel-terms/file`),
  placementRequests: (status: 'pending' | 'resolved') =>
    get<{ items: PlacementRequestItem[] }>(`/placement-requests?status=${status}`),
  approvePlacement: (id: string, note: string) =>
    send<{ status: 'APPROVED' }>('POST', `/placement-requests/${id}/approval`, { note }),
  rejectPlacement: (id: string, note: string) =>
    send<{ status: 'REJECTED' }>('POST', `/placement-requests/${id}/rejection`, { note }),
  setHotelMessage: (stayId: string, text: string) =>
    send<HotelMessage>('PUT', `/stays/${stayId}/hotel-message`, { text }),

  // ── Members and imports ──
  members: (groupId: string) => all<Member>(`/groups/${groupId}/members`),
  cancelMember: (id: string, reason: string) => send<Member>('POST', `/members/${id}/cancellation`, { reason }),
  designateSigner: (id: string, reason: string) => send<Member>('PUT', `/members/${id}/signer`, { reason }),
  registerSigner: (groupId: string, person: NewSigner, reason: string) =>
    send<Member & { username: string | null }>('POST', `/groups/${groupId}/signer`, { ...person, reason }),
  // Guide emergency actions (only for a guide of that group; the API answers 403 for any other).
  guideMembers: (groupId: string) => all<Member>(`/groups/${groupId}/members`),
  /** The group's activation link and its progress, for the guide of that group (DR-405). */
  activationInvitation: (groupId: string) => get<Invitation>(`/groups/${groupId}/activation-invitation`),
  guideAddPassenger: (groupId: string, person: NewSigner, reason: string) =>
    send<Member & { username: string | null }>('POST', `/groups/${groupId}/passengers`, { ...person, reason }),
  guideResetAccess: (groupId: string, memberId: string, reason: string) =>
    send<{ username: string }>('POST', `/groups/${groupId}/passengers/${memberId}/reset-password`, { reason }),
  updateMember: (id: string, changes: MemberChanges, reason: string) =>
    send<Member>('PATCH', `/members/${id}`, { ...changes, reason }),
  previewImport: async (groupId: string, csv: string): Promise<ImportPreview> =>
    (await api.sendRaw<ImportPreview>(`/groups/${groupId}/imports`, csv, 'text/csv; charset=utf-8')).data,
  commitImport: (id: string) => send<ImportCommit>('POST', `/imports/${id}/commit`),

  // ── Distribution ──
  distribution: (stayId: string) => get<Distribution>(`/stays/${stayId}/distribution`),
  assign: (stayId: string, memberId: string, slotId: string, reason: string) =>
    send<unknown>('PUT', `/stays/${stayId}/assignments/${memberId}`, { slotId, reason }),
  release: (stayId: string, memberId: string, reason: string) =>
    send<unknown>('POST', `/stays/${stayId}/assignments/${memberId}/release`, { reason }),
  reopenDistribution: (stayId: string, reason: string) =>
    send<{ stayVersion: number }>('POST', `/stays/${stayId}/distribution-reopenings`, { reason }),
  progress: () =>
    get<{
      items: { stayId: string; status: StayStatus; passengers: number; assigned: number; percentAssigned: number }[];
    }>('/distribution-progress'),
  roster: (stayId: string, documents = false) =>
    get<{ items: RosterEntry[] }>(`/stays/${stayId}/passengers${documents ? '?documents=true' : ''}`),
  administratorExchange: (stayId: string, firstMemberId: string, secondMemberId: string, reason: string) =>
    send<{ stayVersion: number }>('POST', `/stays/${stayId}/administrator-exchanges`, {
      firstMemberId,
      secondMemberId,
      reason,
    }),
  downloadRoomingList: (stayId: string) => downloadCsv(api, `/stays/${stayId}/rooming-list.csv`, 'rooming-list.csv'),
  history: (stayId: string) => get<Page<HistoryEntry>>(`/stays/${stayId}/assignment-history?limit=100`),

  // ── Users and audit ──
  staffUsers: () => get<{ items: StaffUser[] }>('/staff-users'),
  grantRole: (userId: string, role: 'TENANT_ADMIN') =>
    send<StaffUser>('POST', `/staff-users/${userId}/role-grants`, { role }),
  revokeRole: (userId: string, role: StaffRole) =>
    send<undefined>('DELETE', `/staff-users/${userId}/role-grants/${role}`),
  revokeSessions: (userId: string, reason: string) =>
    send<{ revokedSessions: number }>('POST', `/staff-users/${userId}/session-revocations`, { reason }),
  externalUsers: () => get<{ items: ExternalUser[] }>('/external-users'),
  createExternalUser: (body: {
    email: string;
    displayName: string;
    initialPassword: string;
    type: ExternalUser['type'];
  }) => send<ExternalUser>('POST', '/external-users', body),
  grantExternal: (
    userId: string,
    grant:
      { role: 'HOTEL_USER'; stayId: string; reason: string } | { role: 'COORDINATOR'; groupId: string; reason: string },
  ) => send<ExternalUser>('POST', `/external-users/${userId}/grants`, grant),
  revokeExternal: (userId: string, grantId: string, reason: string) =>
    send<undefined>('POST', `/external-users/${userId}/grants/${grantId}/revocation`, { reason }),
  setExternalStatus: (userId: string, status: 'ACTIVE' | 'DISABLED', reason: string) =>
    send<ExternalUser>('PUT', `/external-users/${userId}/status`, { status, reason }),
  resetPassword: (userId: string, newPassword: string, reason: string) =>
    send<undefined>('PUT', `/accounts/${userId}/password`, { newPassword, reason }),
  notifications: () => get<{ items: Notice[]; unread: number; nextBefore: string | null }>('/me/notifications'),
  markNotificationRead: (id: string) => send<undefined>('POST', `/me/notifications/${id}/read`),
  resetSecondFactor: (userId: string, reason: string) =>
    send<undefined>('POST', `/accounts/${userId}/second-factor-reset`, { reason }),
  auditEvents: (filter: { action?: string; from?: string; to?: string; cursor?: string }) =>
    get<Page<AuditEvent>>(`/audit-events${query({ ...filter, limit: '50' })}`),
  hotelAdmins: () => get<{ items: HotelAdmin[] }>('/hotel-admins'),
  createHotelAdmin: (body: { email: string; displayName: string; initialPassword: string; hotelIds: string[] }) =>
    send<HotelAdmin>('POST', '/hotel-admins', body),
  setHotelAdminHotels: (userId: string, hotelIds: string[], reason: string) =>
    send<HotelAdmin>('PUT', `/hotel-admins/${userId}/hotels`, { hotelIds, reason }),
  setHotelAdminStatus: (userId: string, status: 'ACTIVE' | 'DISABLED', reason: string) =>
    send<HotelAdmin>('PUT', `/hotel-admins/${userId}/status`, { status, reason }),
  executiveAudit: (filter: {
    source: 'actions' | 'beds';
    hotelId?: string;
    actorUserId?: string;
    action?: string;
    from?: string;
    to?: string;
    cursor?: string;
  }) => get<Page<ExecutiveAuditItem>>(`/audit-executive${query({ ...filter, limit: '25' })}`),
  executiveAuditActionTypes: () => get<{ items: { value: string; label: string }[] }>('/audit-executive/action-types'),
  accessEvents: (filter: { eventType?: string; cursor?: string }) =>
    get<Page<AccessEvent>>(`/audit-events/access${query({ ...filter, limit: '50' })}`),
  slotChanges: (filter: { cursor?: string }) =>
    get<Page<HistoryEntry>>(`/audit-events/slot-changes${query({ ...filter, limit: '50' })}`),
};
